// Load health for the Room Takeoff page — never let an edit land on state that did not load.
//
// The page reads a revision's rooms, drawn conditions, drawings and room elements in separate calls. A call
// that fails used to leave that list EMPTY (console message only) while everything else stayed editable, so a
// person could keep working on a partial picture, and the automatic syncs (auto walls, generated symbols,
// engineering snapshots) could act on it. This tracker records, per revision, which of those loads succeeded
// and which failed; while any has failed — or before all have loaded once — the page treats the revision as
// read-only (the same switch a locked revision uses) and says why, with a Retry.
//
// Pure reducer: the page owns it with useReducer and dispatches from each loader. Actions carry the revision
// they belong to, so a late answer for a revision the person has left is ignored.

export const CRITICAL_LOADS = Object.freeze({
  rooms: "rooms",
  conditions: "drawn conditions",
  sheets: "drawings",
  elements: "room elements",
});
const KEYS = Object.keys(CRITICAL_LOADS);

export const initialHealth = (revisionId = null) => ({ revisionId, loaded: {}, failed: {} });

export function loadHealthReducer(state, action) {
  if (action.type === "reset") return initialHealth(action.revisionId ?? null);
  // an answer for another revision than the one being tracked starts tracking that revision (the page switched)
  const base = action.revisionId !== state.revisionId ? initialHealth(action.revisionId) : state;
  if (!KEYS.includes(action.key)) return base;
  if (action.type === "ok") {
    const failed = { ...base.failed };
    delete failed[action.key];
    return { ...base, loaded: { ...base.loaded, [action.key]: true }, failed };
  }
  if (action.type === "fail") {
    return { ...base, failed: { ...base.failed, [action.key]: action.message || "the request failed" } };
  }
  return base;
}

/**
 * What the page should do for `revisionId`.
 *   { blocked, failed: [{ key, label, message }], loading }
 * Blocked while any critical load failed, or while some have not completed yet. `loading` (no failure) is the
 * quiet case: nothing to show, just no writes yet.
 */
export function loadStatus(state, revisionId) {
  if (revisionId == null) return { blocked: false, failed: [], loading: false };
  const s = state.revisionId === revisionId ? state : initialHealth(revisionId);
  const failed = KEYS.filter(k => s.failed[k] != null).map(k => ({ key: k, label: CRITICAL_LOADS[k], message: s.failed[k] }));
  const loading = failed.length === 0 && KEYS.some(k => !s.loaded[k]);
  return { blocked: failed.length > 0 || loading, failed, loading };
}

/** One sentence for the banner. */
export function describeLoadFailure(status) {
  if (!status?.failed?.length) return "";
  const what = status.failed.map(f => f.label).join(", ");
  return `Could not load this revision's ${what}. Editing is paused so nothing saved can be overwritten — retry, and if it keeps failing, reload the page.`;
}
